import React, { FC, useCallback, useMemo, useRef } from 'react'
import { Story, TStory } from '../index'
import { FeedForm } from '../../../modules/Feed/components'
import { TStoryActionOption } from '../../../shared/types'
import { scrollToTop } from '../../../shared/utils'
import { useStory } from '../../../modules/Feed/serviceHooks'
import { useConfirmation } from '../../Project/Confirmation'
import { StoryActionsDropdown } from '../blocks/StoryActionsDropdown'
import { ModalAPI } from '../common/StoryShareModel'
import { useRemoveFeed, useUpdateFeed } from '../../../modules/Feed/graphql/mutations'
import { useReactiveVar } from '@apollo/client'
import { activeProjectVar } from '../../../modulesCommon/store'
import { useShareStoryToRO } from '../common/ShareToSuccessStory'
import { subject } from '@casl/ability'
import { AbilityContext } from '../../../shared/abilities'
import { useAbility } from '@casl/react'
import { useStoryPinning } from 'modules/Feed/serviceHooks/useStoryPinning'
import { Tag } from 'shared/__gql-types'

type TLocalizedProjectStoryProps = {
  story: TStory
  dataTestPrefix?: string
  tagsList: {
    id: string
    label?: string | null,
    order_number?: number | null,
  }[]
  handleModal?(api: ModalAPI): () => void
}

export const LocalizedProjectStory: FC<TLocalizedProjectStoryProps> = (props) => {
  const storyRef = useRef<HTMLDivElement>(null)
  const ability = useAbility(AbilityContext)

  const p = useReactiveVar(activeProjectVar)
  const activeProject = { ...p }

  const {
    story: s,
    dataTestPrefix,
    tagsList,
    handleModal
  } = props
  const story = useMemo(() => ({ ...s }), [s])
  const [isEditMode, setEditMode] = React.useState(false)

  const { mutate: updateFeed } = useUpdateFeed()
  const { mutate: removeFeed } = useRemoveFeed()
  const { removeFeedHandler } = useStory({ story, removeFeed, storyRef })
  const { toggleStoryPinState } = useStoryPinning(story, storyRef)

  const [confirmationPopup, shareWithROMutation] = useShareStoryToRO(story, handleModal)
  const [onConfirmDelete, deleteModal] = useConfirmation(() => removeFeedHandler(), {
    header: 'Delete Story',
    actionText: 'Delete'
  })

  const updateFeedHandler = useCallback(
    (args) =>
      updateFeed({
        variables: {
          input: {
            id: +story.id,
            campaign_id: `${story.campaign_id}`,
            ...args,
            tags: args.tags?.map(({ __typename: any, ...rest }: Tag) => rest) ?? [],
          }
        }
      }).then(({ data: { updateFeed } }) => {
        if (!updateFeed.id) {
          // toggleError(true)
        } else {
          scrollToTop()
        }
      }),
    [story, updateFeed]
  )
  const isShared = story.review_status !== null
  const isStoryGlobal = story.global_status === 'PUBLISHED'

  const canEdit = ability.can('edit', subject('Story', story))
  const canDelete = ability.can('delete', subject('Story', story))
  const canBeEdited = !(isStoryGlobal || isShared)
  const canBeDeleted = !(isStoryGlobal || isShared)
  const storyActions = useMemo(() => [
    {
      key: 'pin',
      title: story.pinned ? 'Unpin Story' : 'Pin Story',
      action: () => toggleStoryPinState()
    }, {
      key: 'edit',
      title: 'Edit Story',
      action: () => setEditMode(true),
      hidden: !(canEdit && canBeEdited)
    }, {
      key: 'delete',
      title: 'Delete Story',
      action: () => {
        onConfirmDelete({ data: null, message: 'Are you sure you want to delete this story?' })
      },
      hidden: !(canDelete && canBeDeleted)
    }, {
      key: 'share',
      title: 'Share with RO',
      action: () => {
        shareWithROMutation()
      },
      hidden: story.global_status !== 'NONE'
    }
  ] as TStoryActionOption[], [story.pinned, story.global_status, canEdit, canBeEdited, canDelete, canBeDeleted, toggleStoryPinState, onConfirmDelete, shareWithROMutation])

  const isCommentAdded = story.comments?.some(comment => {
    return String(comment.campaign_id) === activeProject.id
  })
  const contextCanBeAdded = story.global_status === 'PUBLISHED' && !isCommentAdded

  return (
    <Story.Container story={story} storyRef={storyRef}>
      <Story.Header
        actions={
          <StoryActionsDropdown
            actions={storyActions}
          />
        }
      >
        <Story.SharedBookmark />
      </Story.Header>

      {!isEditMode ? (
        <>
          <Story.Body>
            <Story.Article storyRef={storyRef}/>
            <Story.Gallery />
            <Story.Comments
              allowCreation={contextCanBeAdded}
              allowEditing={true}
              allowDeletion={true}
              commentsFilterFn={c => {
                return [
                  activeProject.id,
                  activeProject.parent_id
                ].includes(String(c.campaign_id))
              }}
              assignNewCommentsToProjectId={activeProject.id}
            />
          </Story.Body>

          <Story.Section>
            <Story.Meta />
            <Story.Tags />
          </Story.Section>

        </>
      ) : (
        <FeedForm
          handleCloseForm={() => setEditMode(false)}
          feedHandler={updateFeedHandler}
          tagsList={tagsList}
          initialValue={story.body || ''}
          initialGallery={story.gallery || []}
          initialTitle={story.title}
          initialTagsValue={story.tags || []}
          dataTestPrefix={`${dataTestPrefix}-post_edit`}
          isUpdateFeed
        />
      )}
      {confirmationPopup}
      {deleteModal}
    </Story.Container>
  )
}
